/* 
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import React, {MouseEvent, useCallback} from 'react';
import {makeStyles} from '@material-ui/core';
import {GenericObject, Link} from '@ion/core/src/types/entities';
import {PLATFORM_IDS} from '@ion/core/src/utils/DeviceUtils';

export interface JobIdTitleType {
  pendingJobIds?: GenericObject;
  onJobIdClick?: Function;
  isPendingScript?: boolean;
  isPendingFirmware?: boolean;
  isPendingJpodFW?: boolean;
  isPendingJpodConfig?: boolean;
  isPendingHostedApp?: boolean;
  isVbuFwEnabled?: boolean;
}

const useStyles = makeStyles(() => ({
  groupSections : {
    display       : 'flex',
    alignItems    : 'center',
    letterSpacing : '0.4px'
  },
  groupName : {
    cursor         : 'pointer',
    textDecoration : 'underline'
  }
}));

/**
 * Defines the JobIdTitle component. This component is used to 
 * display the job id's with tooltip when we click on more job Id's link
 *
 * ```typescript
 *
 *   import JobIdTitle from './JobIdTitle';
 *
 *   <JobIdTitle  pendingJobId={pendingJobId} />
 *
 * ```
 */
const JobIdTitle = ({
  pendingJobIds,
  onJobIdClick,
  isPendingScript,
  isPendingFirmware,
  isPendingJpodFW,
  isPendingJpodConfig,
  isPendingHostedApp,
  isVbuFwEnabled = false
}: JobIdTitleType) => {
  const classes            = useStyles();
  const pendingJobIdLength = pendingJobIds?.length || 0;

  const onTitleClick = useCallback((group: Link) => (e: MouseEvent<HTMLDivElement>) => {
    onJobIdClick(group);
  }, []);
  
  if (pendingJobIdLength) {
    return (
      <>
        {
          pendingJobIds?.slice(1)?.map(job => {
            if (job?.platformId === PLATFORM_IDS.firmware && isPendingFirmware) {
              return (
                <div className={classes.groupSections}
                  onClick={onTitleClick(job)}>
                  <span>{job?.newVersion ? `[${job?.newVersion}]` : ' ' }</span>
                  &nbsp;<span className={classes.groupName}>{job?.jobId}</span>
                </div>
              );
            }
            if ((job?.platformId === PLATFORM_IDS.config || job?.platformId === PLATFORM_IDS.peg2) && isPendingScript) {
              return (
                <div className={classes.groupSections}
                  onClick={onTitleClick(job)}>
                  <span>{job?.newVersion ? `[${job?.newVersion}]` : ' ' }</span>
                  &nbsp;<span className={classes.groupName}>{job?.jobId}</span>
                </div>
              );
            }
            if (((job?.platformId === PLATFORM_IDS.jpodFirmware) ||
                (job?.platformId === PLATFORM_IDS.obd1Type1Firmware && isVbuFwEnabled) ||
                (job?.platformId === PLATFORM_IDS.obd2Type2Firmware && isVbuFwEnabled) ||
                (job?.platformId === PLATFORM_IDS.vbu3Firmware && isVbuFwEnabled) ||
                (job?.platformId === PLATFORM_IDS.jpod2Firmware)) &&
                isPendingJpodFW) {
              return (
                <div className={classes.groupSections}
                  onClick={onTitleClick(job)}>
                  <span>{job?.newVersion ? `[${job?.newVersion}]` : ' ' }</span>
                  &nbsp;<span className={classes.groupName}>{job?.jobId}</span>
                </div>
              );
            }
            if (((job?.platformId === PLATFORM_IDS.jpodConfig) ||
                (job?.platformId === PLATFORM_IDS.jpod2Config)) &&
                isPendingJpodConfig) {
              return (
                <div className={classes.groupSections}
                  onClick={onTitleClick(job)}>
                  <span>{job?.newVersion ? `[${job?.newVersion}]` : ' ' }</span>
                  &nbsp;<span className={classes.groupName}>{job?.jobId}</span>
                </div>
              );
            }
            if ((job?.platformId === PLATFORM_IDS.hostedAppFirmware ||
                job?.platformId === PLATFORM_IDS.hostedAppConfig ||
                job?.platformId === PLATFORM_IDS.hostedAppV2Firmware ||
                job?.platformId === PLATFORM_IDS.hostedAppApnList ||
                job?.platformId === PLATFORM_IDS.hostedAppFirmwareFirstPeripheral ||
                job?.platformId === PLATFORM_IDS.hostedAppConfigFirstPeripheral ||
                job?.platformId === PLATFORM_IDS.hostedAppFirmware2ndPeripheral ||
                job?.platformId === PLATFORM_IDS.hostedAppConfig2ndPeripheral ||
                job?.platformId === PLATFORM_IDS.hostedAppV3Firmware ||
                job?.platformId === PLATFORM_IDS.hostedAppV4Firmware ||
                job?.platformId === PLATFORM_IDS.hostedAppV5Firmware ||
                job?.platformId === PLATFORM_IDS.hostedAppV6Firmware ||
                job?.platformId === PLATFORM_IDS.edgeAppFirmware) &&
                isPendingHostedApp) {
              return (
                <div className={classes.groupSections}
                  onClick={onTitleClick(job)}>
                  <span>{job?.newVersion ? `[${job?.newVersion}]` : ' ' }</span>
                  &nbsp;<span className={classes.groupName}>{job?.jobId}</span>
                </div>
              );
            }
          })
        }
      </>
    );
  }
  return <div />;
};

export default JobIdTitle;
